import { useEffect, useState, useCallback } from 'react';
import { disputesApi, evidenceApi, mlApi, type Evidence } from '../api/client';
import clsx from 'clsx';
import MLScoreDisplay from '../components/MLScoreDisplay';
import EvidenceUpload from '../components/EvidenceUpload';
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

// TypeScript interfaces for proper typing
interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  aggregator_order_id: string;
  order_date: string;
  total_amount: number;
  refund_amount: number;
  refund_reason: string;
  refund_items: OrderItem[];
}

interface Dispute {
  id: string;
  order_id: string;
  order?: Order;
  store_name: string;
  aggregator?: string;
  bucket: string;
  confidence_score: number;
  status: DisputeStatus;
  draft_text: string;
  final_text?: string;
  created_at: string;
  submitted_at?: string;
  resolved_at?: string;
  recovered_amount?: number;
  email_message_id?: string;
}

type DisputeStatus = 'draft' | 'pending_review' | 'approved' | 'submitted' | 'won' | 'lost' | 'expired';

interface FilterState {
  status: string;
  bucket: string;
}

const BUCKET_COLORS: Record<string, string> = {
  B1: '#3b82f6',
  B2: '#8b5cf6',
  B3: '#f59e0b',
  B4: '#ef4444',
};

const BUCKET_NAMES: Record<string, string> = {
  B1: 'High Item Missing',
  B2: 'High Value Refund',
  B3: 'Full Order Refund',
  B4: 'Repeat Offender',
};

const BUCKET_DESCRIPTIONS: Record<string, string> = {
  B1: 'More than 50% of order items were marked as missing - unusually high for a single order',
  B2: 'Refund amount exceeds 50% of order value ($25+) - potential abuse of partial refunds',
  B3: 'Full order refund ($20+) claimed - customer received nothing but payment was reversed',
  B4: 'Customer has 3+ refund claims in 30 days - pattern of repeated refund requests',
};

function getDaysWaiting(submittedAt: string | undefined): number | null {
  if (!submittedAt) return null;
  const submitted = new Date(submittedAt);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - submitted.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

function formatDaysWaiting(days: number | null): string {
  if (days === null) return '';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

function generateDefaultDraft(dispute: Dispute): string {
  const order = dispute.order;
  if (!order) return '';

  const items = order.refund_items?.map(i => `- ${i.name} (x${i.quantity})`).join('\n') || 'All items';

  return `Dear Support Team,

I am disputing the refund issued for Order #${order.aggregator_order_id}.

Order Details:
- Order Date: ${new Date(order.order_date).toLocaleDateString()}
- Order Total: $${order.total_amount.toFixed(2)}
- Refund Amount: $${order.refund_amount.toFixed(2)}
- Claimed Issue: ${order.refund_reason || 'Not specified'}

Items in question:
${items}

Our Response:
This order was prepared correctly and in full according to the customer's specifications. All items were carefully packaged and verified before handoff to the delivery driver.

We have documentation including timestamped preparation photos and sealed packaging to support that the order left our establishment complete and in proper condition.

We request a full review of this refund and reinstatement of the charged amount.

Thank you for your attention to this matter.

Best regards,
Restaurant Management`;
}

export default function Disputes() {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [filter, setFilter] = useState<FilterState>({ status: '', bucket: '' });

  useEffect(() => {
    fetchDisputes();
  }, [filter]);

  async function fetchDisputes() {
    try {
      setLoading(true);
      const params: Record<string, string> = { limit: '50' };
      if (filter.status) params.status = filter.status;
      if (filter.bucket) params.bucket = filter.bucket;

      const res = await disputesApi.list(params);
      setDisputes(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    } finally {
      setLoading(false);
    }
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading disputes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dispute Queue</h1>
          <p className="text-slate-500 mt-1">Review and submit flagged refund disputes</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="pending_review">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="submitted">Submitted</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
          <select
            value={filter.bucket}
            onChange={(e) => setFilter({ ...filter, bucket: e.target.value })}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="">All Buckets</option>
            <option value="B1">B1 - High Item Missing</option>
            <option value="B2">B2 - High Value</option>
            <option value="B3">B3 - Full Order</option>
            <option value="B4">B4 - Repeat Offender</option>
          </select>
        </div>
      </div>

      {/* Disputes Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Store
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Order Total
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Refund
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Bucket
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Rules Score
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  ML Score
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {disputes.map((dispute) => (
                <tr key={dispute.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm text-slate-900">
                      {dispute.order?.aggregator_order_id?.slice(0, 15) || dispute.id.slice(0, 8)}...
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900">{dispute.store_name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900">
                      {formatCurrency(dispute.order?.total_amount || 0)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-red-600">
                      {formatCurrency(dispute.order?.refund_amount || 0)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <BucketBadge bucket={dispute.bucket} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full"
                          style={{ width: `${(dispute.confidence_score || 0) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 font-medium">
                        {Math.round((dispute.confidence_score || 0) * 100)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <MLScoreDisplay disputeId={dispute.id} compact />
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={dispute.status} submittedAt={dispute.submitted_at} />
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => setSelectedDispute(dispute)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors"
                    >
                      <DocumentTextIcon className="w-4 h-4" />
                      Review
                    </button>
                  </td>
                </tr>
              ))}
              {disputes.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                        <ExclamationTriangleIcon className="w-8 h-8 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-slate-900 font-medium">No disputes found</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {filter.status || filter.bucket
                            ? 'Try adjusting your filters'
                            : 'New flagged orders will appear here'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dispute Detail Modal */}
      {selectedDispute && (
        <DisputeModal
          dispute={selectedDispute}
          onClose={() => setSelectedDispute(null)}
          onUpdate={fetchDisputes}
        />
      )}
    </div>
  );
}

interface DisputeModalProps {
  dispute: Dispute;
  onClose: () => void;
  onUpdate: () => void;
}

const PLATFORM_PORTALS: Record<string, { name: string; url: string; color: string }> = {
  ubereats: { name: 'Uber Eats Manager', url: 'https://merchants.ubereats.com', color: '#06C167' },
  doordash: { name: 'DoorDash Merchant Portal', url: 'https://merchant.doordash.com', color: '#FF3008' },
  grubhub: { name: 'Grubhub for Restaurants', url: 'https://restaurant.grubhub.com', color: '#F63440' },
};

function DisputeModal({ dispute, onClose, onUpdate }: DisputeModalProps) {
  const [draftText, setDraftText] = useState(dispute.draft_text || generateDefaultDraft(dispute));
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [outcome, setOutcome] = useState<'won' | 'lost' | 'partial'>('won');
  const [recoveredAmount, setRecoveredAmount] = useState(dispute.order?.refund_amount?.toString() || '0');

  // Email submission state (Grubhub only)
  const [showEmailSubmit, setShowEmailSubmit] = useState(false);
  const [restaurantEmail, setRestaurantEmail] = useState('');
  const [grubhubAccountId, setGrubhubAccountId] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);

  // Evidence state
  const [evidence, setEvidence] = useState<Evidence[]>([]);

  // AI generation state
  const [generatingAI, setGeneratingAI] = useState(false);

  const handleGenerateAIDraft = async () => {
    setGeneratingAI(true);
    try {
      const res = await mlApi.getEnhancedRecommendation(dispute.id);
      if (res.data.suggestedText) {
        setDraftText(res.data.suggestedText);
      }
    } catch (err) {
      console.error('Failed to generate AI draft:', err);
    } finally {
      setGeneratingAI(false);
    }
  };

  const fetchEvidence = useCallback(async () => {
    try {
      const res = await evidenceApi.list(dispute.id);
      setEvidence(res.data.evidence);
    } catch (err) {
      console.error('Failed to fetch evidence:', err);
    }
  }, [dispute.id]);

  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  // Detect platform from aggregator field or store name
  const platform = dispute.aggregator?.toLowerCase()
    || (dispute.store_name?.toLowerCase().includes('uber') ? 'ubereats'
    : dispute.store_name?.toLowerCase().includes('doordash') ? 'doordash'
    : dispute.store_name?.toLowerCase().includes('grubhub') ? 'grubhub'
    : 'ubereats');
  const portalInfo = PLATFORM_PORTALS[platform];
  const isGrubhub = platform === 'grubhub';

  async function handleCopyToClipboard() {
    try {
      await navigator.clipboard.writeText(draftText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await disputesApi.update(dispute.id, { draft_text: draftText });
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  }

  async function handleMarkSubmitted() {
    setSaving(true);
    try {
      await disputesApi.update(dispute.id, { draft_text: draftText, status: 'submitted' });
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to submit:', error);
    } finally {
      setSaving(false);
    }
  }

  async function handleRecordOutcome() {
    setSaving(true);
    try {
      await disputesApi.outcome(dispute.id, {
        outcome,
        recovered_amount: outcome === 'lost' ? 0 : parseFloat(recoveredAmount) || 0,
      });
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to record outcome:', error);
    } finally {
      setSaving(false);
    }
  }

  async function handleEmailSubmit() {
    if (!restaurantEmail) {
      setEmailError('Restaurant email is required');
      return;
    }

    setEmailSending(true);
    setEmailError(null);

    try {
      await disputesApi.emailSubmit(dispute.id, {
        restaurant_email: restaurantEmail,
        grubhub_account_id: grubhubAccountId || undefined,
        dispute_text: draftText,
      });
      setEmailSuccess(true);
      setTimeout(() => {
        onUpdate();
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to send email:', error);
      setEmailError(error instanceof Error ? error.message : 'Failed to send dispute email');
    } finally {
      setEmailSending(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Dispute Review</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Order {dispute.order?.aggregator_order_id?.slice(0, 15) || dispute.id.slice(0, 8)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Order Details */}
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4">
                  Order Details
                </h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-500">Store</dt>
                    <dd className="text-sm font-medium text-slate-900">{dispute.store_name}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-500">Order ID</dt>
                    <dd className="text-sm font-mono text-slate-900">
                      {dispute.order?.aggregator_order_id || 'N/A'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-500">Date</dt>
                    <dd className="text-sm text-slate-900">
                      {dispute.order?.order_date
                        ? new Date(dispute.order.order_date).toLocaleDateString()
                        : 'N/A'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-500">Total</dt>
                    <dd className="text-sm font-medium text-slate-900">
                      {formatCurrency(dispute.order?.total_amount || 0)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-500">Refund</dt>
                    <dd className="text-sm font-medium text-red-600">
                      {formatCurrency(dispute.order?.refund_amount || 0)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-500">Reason</dt>
                    <dd className="text-sm text-slate-900">
                      {dispute.order?.refund_reason || 'Not specified'}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Items Flagged */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4">
                  Items Flagged
                </h3>
                {dispute.order?.refund_items && dispute.order.refund_items.length > 0 ? (
                  <ul className="space-y-2">
                    {dispute.order.refund_items.map((item: OrderItem, i: number) => (
                      <li key={i} className="flex justify-between text-sm">
                        <span className="text-slate-700">
                          {item.name} <span className="text-slate-400">x{item.quantity}</span>
                        </span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(item.price * item.quantity)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500">No items specified</p>
                )}
              </div>

              {/* Fraud Indicators */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4">
                  Fraud Indicators
                </h3>
                <div className="flex items-center gap-4 mb-3">
                  <span
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold"
                    style={{
                      backgroundColor: `${BUCKET_COLORS[dispute.bucket]}15`,
                      color: BUCKET_COLORS[dispute.bucket],
                    }}
                  >
                    {dispute.bucket} - {BUCKET_NAMES[dispute.bucket] || 'Unknown'}
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full"
                        style={{ width: `${(dispute.confidence_score || 0) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-slate-600">
                      {Math.round((dispute.confidence_score || 0) * 100)}% rules confidence
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-600 bg-white p-3 rounded-lg border border-slate-200">
                  {BUCKET_DESCRIPTIONS[dispute.bucket] || 'This order was flagged for potential refund abuse.'}
                </p>
              </div>

              {/* ML Win Prediction */}
              <MLScoreDisplay disputeId={dispute.id} showDetails />
            </div>

            {/* Dispute Draft */}
            <div className="space-y-6">
              {/* Platform Portal Link */}
              <div className="p-4 rounded-xl border" style={{ borderColor: `${portalInfo.color}40`, backgroundColor: `${portalInfo.color}08` }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Submit via {portalInfo.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Copy the text below and paste it in the dispute form</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isGrubhub && dispute.status !== 'submitted' && dispute.status !== 'won' && dispute.status !== 'lost' && (
                      <button
                        onClick={() => setShowEmailSubmit(!showEmailSubmit)}
                        className={clsx(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors border',
                          showEmailSubmit
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                        )}
                      >
                        <EnvelopeIcon className="w-4 h-4" />
                        Email Submit
                      </button>
                    )}
                    <a
                      href={portalInfo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                      style={{ backgroundColor: portalInfo.color, color: 'white' }}
                    >
                      Open Portal
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Grubhub Email Submission Form */}
              {isGrubhub && showEmailSubmit && dispute.status !== 'submitted' && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100">
                      <EnvelopeIcon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-900">Submit via Email</h4>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        Grubhub accepts disputes via email. We'll send on your behalf to restaurantcare@grubhub.com
                      </p>
                    </div>
                  </div>

                  {emailSuccess ? (
                    <div className="flex items-center gap-2 p-3 bg-emerald-100 rounded-lg">
                      <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700">
                        Dispute email sent successfully! Redirecting...
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 mb-1">
                          Restaurant Contact Email *
                        </label>
                        <input
                          type="email"
                          value={restaurantEmail}
                          onChange={(e) => setRestaurantEmail(e.target.value)}
                          placeholder="owner@restaurant.com"
                          className="w-full px-3 py-2 text-sm border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                        <p className="text-xs text-emerald-600 mt-1">
                          Grubhub will reply to this email address
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 mb-1">
                          Grubhub Account ID (optional)
                        </label>
                        <input
                          type="text"
                          value={grubhubAccountId}
                          onChange={(e) => setGrubhubAccountId(e.target.value)}
                          placeholder="Found in your Grubhub portal"
                          className="w-full px-3 py-2 text-sm border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>

                      {emailError && (
                        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                          <XCircleIcon className="w-4 h-4 text-red-500" />
                          <span className="text-xs text-red-700">{emailError}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span className="text-xs text-amber-700">
                          By submitting, you authorize RecoverRefunds to act as your agent
                        </span>
                      </div>

                      <button
                        onClick={handleEmailSubmit}
                        disabled={emailSending || !restaurantEmail}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-lg text-sm font-medium hover:from-emerald-700 hover:to-emerald-600 shadow-lg shadow-emerald-500/25 disabled:opacity-50 transition-all"
                      >
                        {emailSending ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <PaperAirplaneIcon className="w-4 h-4" />
                            Send Dispute Email
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                    Dispute Draft
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleGenerateAIDraft}
                      disabled={generatingAI}
                      className={clsx(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                        generatingAI
                          ? 'bg-orange-100 text-orange-700 cursor-wait'
                          : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      )}
                    >
                      <SparklesIcon className={clsx('w-4 h-4', generatingAI && 'animate-pulse')} />
                      {generatingAI ? 'Generating...' : 'AI Generate'}
                    </button>
                    <button
                      onClick={handleCopyToClipboard}
                      className={clsx(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                        copied
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      )}
                    >
                      {copied ? (
                        <>
                          <CheckCircleIcon className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="w-4 h-4" />
                          Copy to Clipboard
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <textarea
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  className="w-full h-64 px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-mono text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  placeholder="Dispute text will be generated..."
                />
              </div>

              {/* Evidence Upload */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4">
                  Evidence
                </h3>
                <EvidenceUpload
                  disputeId={dispute.id}
                  evidence={evidence}
                  onUpdate={fetchEvidence}
                />
              </div>

              {/* Workflow Status & Next Steps */}
              {dispute.status !== 'won' && dispute.status !== 'lost' && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 mb-4">
                  <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-600 text-white text-xs font-bold">?</span>
                    What to do next
                  </h4>
                  <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
                    <li>Review and edit the dispute text above</li>
                    <li>Click "Copy to Clipboard" then open the platform portal</li>
                    <li>Find this order in the portal and paste the dispute</li>
                    <li>After submitting to the platform, click "Mark as Submitted" below</li>
                    <li>Once you hear back, record the outcome (won/lost)</li>
                  </ol>
                </div>
              )}

              {/* Submission Checklist */}
              {dispute.status !== 'won' && dispute.status !== 'lost' && dispute.status !== 'submitted' && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-900 mb-2">Before marking as submitted:</h4>
                  <ul className="text-xs text-amber-800 space-y-1">
                    <li className="flex items-center gap-2">
                      <input type="checkbox" className="rounded text-amber-600" />
                      <span>Copied dispute text to clipboard</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <input type="checkbox" className="rounded text-amber-600" />
                      <span>Opened platform portal and found the order</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <input type="checkbox" className="rounded text-amber-600" />
                      <span>Pasted dispute text and submitted to platform</span>
                    </li>
                  </ul>
                </div>
              )}

              {/* Submitted - Waiting for outcome */}
              {dispute.status === 'submitted' && (
                <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <h4 className="text-sm font-semibold text-orange-900 mb-2 flex items-center gap-2">
                    <span className="animate-pulse">⏳</span>
                    Awaiting Platform Response
                    {dispute.submitted_at && (
                      <span className="ml-auto text-xs font-normal bg-amber-100 px-2 py-0.5 rounded-full">
                        {formatDaysWaiting(getDaysWaiting(dispute.submitted_at))} waiting
                      </span>
                    )}
                  </h4>
                  <p className="text-xs text-orange-800">
                    This dispute was submitted to the platform. Once you receive a response (typically 5-14 days),
                    click "Record Outcome" to track whether you won or lost.
                  </p>
                  {getDaysWaiting(dispute.submitted_at) !== null && getDaysWaiting(dispute.submitted_at)! > 14 && (
                    <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                      <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                      Taking longer than usual. Consider following up with the platform.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            {dispute.status === 'submitted' && (
              <button
                onClick={() => setShowOutcomeModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-amber-500/25 transition-all"
              >
                <CurrencyDollarIcon className="w-4 h-4" />
                Record Outcome
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors"
            >
              Cancel
            </button>
            {dispute.status !== 'won' && dispute.status !== 'lost' && dispute.status !== 'submitted' && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  onClick={handleMarkSubmitted}
                  disabled={saving}
                  className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl text-sm font-medium hover:from-emerald-700 hover:to-emerald-600 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all disabled:opacity-50"
                >
                  <span className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Mark as Submitted'}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Outcome Recording Modal */}
        {showOutcomeModal && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Record Dispute Outcome</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Outcome</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setOutcome('won')}
                      className={clsx(
                        'px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all',
                        outcome === 'won'
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      )}
                    >
                      Won
                    </button>
                    <button
                      onClick={() => setOutcome('partial')}
                      className={clsx(
                        'px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all',
                        outcome === 'partial'
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      )}
                    >
                      Partial
                    </button>
                    <button
                      onClick={() => setOutcome('lost')}
                      className={clsx(
                        'px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all',
                        outcome === 'lost'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      )}
                    >
                      Lost
                    </button>
                  </div>
                </div>

                {outcome !== 'lost' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Recovered Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="number"
                        value={recoveredAmount}
                        onChange={(e) => setRecoveredAmount(e.target.value)}
                        className="w-full pl-8 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-amber-500"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Original refund: {formatCurrency(dispute.order?.refund_amount || 0)}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowOutcomeModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordOutcome}
                  disabled={saving}
                  className="px-4 py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl text-sm font-medium hover:from-amber-700 hover:to-amber-600 shadow-lg shadow-amber-500/25 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Outcome'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BucketBadge({ bucket }: { bucket: string }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-flex">
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-help"
        style={{
          backgroundColor: `${BUCKET_COLORS[bucket]}15`,
          color: BUCKET_COLORS[bucket],
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {bucket}
      </span>
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-lg">
          <p className="font-semibold mb-1">{BUCKET_NAMES[bucket]}</p>
          <p className="text-slate-300">{BUCKET_DESCRIPTIONS[bucket]}</p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, submittedAt }: { status: string; submittedAt?: string }) {
  const styles: Record<string, { bg: string; text: string; icon?: React.ComponentType<{ className?: string }> }> = {
    draft: { bg: 'bg-slate-100', text: 'text-slate-700' },
    pending_review: { bg: 'bg-amber-100', text: 'text-amber-700' },
    approved: { bg: 'bg-amber-100', text: 'text-amber-700' },
    submitted: { bg: 'bg-amber-100', text: 'text-amber-700' },
    won: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircleIcon },
    lost: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircleIcon },
    expired: { bg: 'bg-slate-100', text: 'text-slate-700' },
  };

  const style = styles[status] || styles.draft;
  const Icon = style.icon;
  const daysWaiting = status === 'submitted' ? getDaysWaiting(submittedAt) : null;

  return (
    <div className="flex flex-col gap-1">
      <span
        className={clsx(
          'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium capitalize',
          style.bg,
          style.text
        )}
      >
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {status.replace('_', ' ')}
      </span>
      {daysWaiting !== null && (
        <span className="text-xs text-slate-500 pl-1">
          {formatDaysWaiting(daysWaiting)} ago
        </span>
      )}
    </div>
  );
}
